import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useGeolocation } from "../hooks/useGeolocation";
import { useT } from "../lib/lang";
import { getStyle, applySatelliteZoomCap } from "../lib/mapStyles";
import { getCountryBoundary, installCountryMask } from "../lib/countryMask";
import { fetchSafetyHeatmap, API } from "../lib/api";
import { SERVICE_CATEGORIES, buildServicesGeoJSON, EMERGENCY_SERVICES, SERVICE_META } from "../data/maliEmergencyServices";
import { useFacilities } from "../lib/useFacilities";
import { LiveSharePanel, useLiveShareSession } from "../components/LiveSharePanel";
import { RouteDrawer } from "../components/RouteDrawer";
import { addRouteLayer, requestRoute, renderRouteOnMap, clearRoute } from "../lib/mapTraffic";
import { installBuildingExtrusion } from "../lib/mapStyles";
import { toast } from "sonner";
import { Layers, Navigation2, Flame as FlameIcon, ShieldCheck, AlertTriangle, MapPin, Radio, Car, Construction, Search, X as XIcon } from "lucide-react";
import {
  CITIZEN_FALLBACK,
  USER_SOURCE, SVC_SOURCE, HEAT_SOURCE, PIN_SOURCE,
  TOMTOM_INC_SRC, CROWD_ACC_SRC, TRAFFIC_RASTER,
  tomtomMeta, accColor, hasWebGL, buildPulsingDot, buildServiceIcon,
} from "../lib/mapPageHelpers";

export default function MapPage() {
  const { coords, error, refresh: refreshGps } = useGeolocation();
  const [t] = useT();

  const [layer, setLayer] = useState("hybrid");
  // Iter70 — single map-style control (one button → popover). Replaces
  // the prior 3-button segmented strip so the top-right corner of the
  // map carries exactly ONE control, not three. The popover anchors
  // under the button and lists Plan / Satellite / Sombre.
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [webglOK] = useState(hasWebGL);
  // Iter75 — Client-side POI search across `EMERGENCY_SERVICES`.
  // Filters by name / city / category (accent-insensitive). Tapping a
  // result flies the map to the POI and closes the dropdown.
  const [searchQuery, setSearchQuery] = useState("");

  // Iter78 — Live facility data from `/api/facilities` (Mongo-backed;
  // seeded from OSM Overpass + curated Mali list). Falls back to the
  // hardcoded EMERGENCY_SERVICES if the API is unreachable.
  const { items: facilities } = useFacilities({ countries: "ML,GN" });

  // Which service categories are visible (default all on)
  const [activeServiceKeys, setActiveServiceKeys] = useState(
    () => new Set(SERVICE_CATEGORIES.map((s) => s.key))
  );
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  // ── Traffic layers ─────────────────────────────────────────────────────
  const [trafficFlowOn, setTrafficFlowOn] = useState(false);     // TomTom raster tiles
  const [tomtomIncidentsOn, setTomtomIncidentsOn] = useState(false);
  const [crowdAccidentsOn, setCrowdAccidentsOn] = useState(true); // free, ON by default
  const [livePanelOpen, setLivePanelOpen] = useState(false);
  // Iter68 — Long-press / right-click "report from arbitrary location"
  // was removed at user request: emergency alerts must come from the
  // citizen's real GPS through the SOS flow, never from a manually
  // picked map point. We keep the variable as a stable `null` to
  // minimize churn in the marker-data + report-pin-card branches
  // below: those branches now resolve to "no pin" forever and the
  // related UI is gone.
  const reportPin = null;

  // Turn-by-turn routing
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeDest, setRouteDest] = useState(null);   // {name,city,lat,lng}

  const liveShare = useLiveShareSession(coords);

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const flewOnceRef = useRef(false);
  // Iter72 — Track whether the user changed the map style since mount.
  // The map is constructed with `getStyle(layer)` directly, so the
  // first run of the [layer] effect would re-call setStyle for no
  // reason. Previously this was gated on `flewOnceRef`, but that flag
  // only flips when GPS gets a fix — so users with GPS denied could
  // never switch styles. This dedicated ref skips ONLY the initial
  // mount and lets every subsequent change through.
  const layerMountedRef = useRef(false);
  const accColorRef = useRef(accColor(null));

  // ── Turn-by-turn routing from current GPS to a service ───────────────
  // Declared BEFORE the mount-time useEffect that wires the service-marker
  // popup click handler — otherwise the closure reference at the popup
  // wire-up site sits in the temporal dead-zone of this const (lint:
  // react-hooks/immutability "cannot access variable before declaration").
  const startRouteToService = async (svc) => {
    if (!coords) {
      toast.error("Position GPS requise pour calculer un itinéraire");
      return;
    }
    setRouteDest({ name: svc.name, city: svc.city, lat: svc.lat, lng: svc.lng });
    setRouteData(null);
    setRouteLoading(true);
    setRouteOpen(true);
    try {
      const r = await requestRoute({
        originLat: coords.latitude,
        originLng: coords.longitude,
        destLat: svc.lat,
        destLng: svc.lng,
      });
      setRouteData(r);
      const map = mapRef.current;
      if (map) renderRouteOnMap(map, r);
    } catch (e) {
      toast.error("Impossible de calculer l'itinéraire");
      setRouteOpen(false);
    } finally {
      setRouteLoading(false);
    }
  };

  // ── Overlay sources/layers (GPS dot, POI services, heatmap, route,
  // traffic, incidents, crowd-accidents) ────────────────────────────────
  // Iter82 bug fix: `map.setStyle()` (called on every Plan/Satellite/
  // Sombre switch) wipes ALL custom sources/layers — previously only
  // USER_SOURCE (the GPS dot) was re-added afterwards, so hospitals/
  // police/embassies (SVC_SOURCE) and every other overlay silently
  // vanished the moment a citizen left the initial Satellite style.
  // Extracted so the exact same re-creation logic runs both at mount
  // AND after every `styledata` following a style swap. Each block is
  // guarded by `!map.getSource(...)` so it's a safe no-op when the
  // layer already exists.
  const installOverlayLayers = (map) => {
    if (!map.getSource(USER_SOURCE)) {
      map.addSource(USER_SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "user-accuracy",
        type: "circle",
        source: USER_SOURCE,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["coalesce", ["get", "accuracy"], 0], 0, 10, 20, 18, 100, 36, 500, 64],
          "circle-color": ["get", "ringColor"],
          "circle-opacity": 0.18,
          "circle-stroke-color": ["get", "ringColor"],
          "circle-stroke-width": 1.5,
          "circle-stroke-opacity": 0.55,
        },
      });
      if (!map.hasImage("pulse-dot")) {
        map.addImage("pulse-dot", buildPulsingDot(map, 100, accColorRef.current), { pixelRatio: 2 });
      }
      map.addLayer({ id: "user-dot", type: "symbol", source: USER_SOURCE, layout: { "icon-image": "pulse-dot", "icon-allow-overlap": true } });
      if (coords) {
        map.getSource(USER_SOURCE).setData({
          type: "FeatureCollection",
          features: [{
            type: "Feature",
            properties: { accuracy: coords.accuracy || 0, ringColor: accColorRef.current },
            geometry: { type: "Point", coordinates: [coords.longitude, coords.latitude] },
          }],
        });
      }
    }

    // Services (hospitals / police / fire / embassies…) — the layer this
    // bug fix is specifically about.
    if (!map.getSource(SVC_SOURCE)) {
      // Iter84 — Native MapLibre clustering: dense areas (Bamako
      // centre) collapse overlapping POI icons into a numbered badge
      // below zoom 14; zooming past that (or tapping a cluster) reveals
      // the individual hospital/police/fire/embassy icons again.
      map.addSource(SVC_SOURCE, {
        type: "geojson",
        data: buildServicesGeoJSON(activeServiceKeys, facilities),
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
      // Iter83 — Real glyph badge icons (hospital cross, police shield,
      // fire flame, FAMA star, embassy flag) registered once per
      // category, instead of a plain colored `circle` layer.
      SERVICE_CATEGORIES.forEach((s) => {
        const imgId = `svc-icon-${s.key}`;
        if (!map.hasImage(imgId)) {
          map.addImage(imgId, buildServiceIcon(s.key, s.color, 64), { pixelRatio: 2 });
        }
      });
      map.addLayer({
        id: "svc-clusters",
        type: "circle",
        source: SVC_SOURCE,
        filter: ["has", "point_count"],
        paint: {
          "circle-radius": ["step", ["get", "point_count"], 16, 5, 20, 15, 24],
          "circle-color": "#0F1428",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2.5,
        },
      });
      map.addLayer({
        id: "svc-cluster-count",
        type: "symbol",
        source: SVC_SOURCE,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": 12,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-allow-overlap": true,
        },
        paint: { "text-color": "#ffffff" },
      });
      map.addLayer({
        id: "svc-bg",
        type: "symbol",
        source: SVC_SOURCE,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-image": ["concat", "svc-icon-", ["get", "category"]],
          "icon-size": 1,
          "icon-allow-overlap": true,
          "icon-anchor": "center",
        },
      });
      map.addLayer({
        id: "svc-label",
        type: "symbol",
        source: SVC_SOURCE,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "text-field": ["get", "name"],
          "text-size": 10,
          "text-offset": [0, 1.6],
          "text-anchor": "top",
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: {
          "text-color": "#0F172A",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      });
    }

    // Safety heatmap
    if (!map.getSource(HEAT_SOURCE)) {
      const cachedFeatures = (heatmapData?.cells || []).map((c) => ({
        type: "Feature",
        properties: { count: c.count },
        geometry: { type: "Point", coordinates: [c.longitude, c.latitude] },
      }));
      map.addSource(HEAT_SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: cachedFeatures } });
      map.addLayer(
        {
          id: "safety-heat",
          type: "heatmap",
          source: HEAT_SOURCE,
          maxzoom: 14,
          paint: {
            "heatmap-weight": ["interpolate", ["linear"], ["coalesce", ["get", "count"], 1], 1, 0.4, 30, 1.4],
            "heatmap-intensity": 1.1,
            "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 5, 14, 12, 36],
            "heatmap-opacity": 0.55,
            "heatmap-color": [
              "interpolate", ["linear"], ["heatmap-density"],
              0,    "rgba(0,0,0,0)",
              0.15, "rgba(34,197,94,0.55)",
              0.45, "rgba(234,179,8,0.65)",
              0.75, "rgba(234,88,12,0.7)",
              1,    "rgba(220,38,38,0.85)",
            ],
          },
          layout: { visibility: heatmapOn ? "visible" : "none" },
        },
        "user-accuracy"
      );
    }

    // Report pin (kept for API-compat even though long-press is disabled)
    if (!map.getSource(PIN_SOURCE)) {
      map.addSource(PIN_SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "report-pin-ring",
        type: "circle",
        source: PIN_SOURCE,
        paint: { "circle-radius": 22, "circle-color": "#DC2626", "circle-opacity": 0.2, "circle-stroke-color": "#DC2626", "circle-stroke-width": 2 },
      });
      map.addLayer({
        id: "report-pin-dot",
        type: "circle",
        source: PIN_SOURCE,
        paint: { "circle-radius": 10, "circle-color": "#DC2626", "circle-stroke-color": "#ffffff", "circle-stroke-width": 3 },
      });
    }

    // Route polyline (already idempotent — guards internally)
    addRouteLayer(map);
    if (routeData) renderRouteOnMap(map, routeData);

    // TomTom Traffic Flow raster
    if (!map.getSource(TRAFFIC_RASTER)) {
      map.addSource(TRAFFIC_RASTER, {
        type: "raster",
        tiles: [`${API}/map/traffic/flow/{z}/{x}/{y}.png?tileSize=256&thickness=8`],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 22,
        attribution: "Trafic © TomTom",
      });
      map.addLayer(
        {
          id: "tomtom-flow-layer",
          type: "raster",
          source: TRAFFIC_RASTER,
          paint: { "raster-opacity": 0.78 },
          layout: { visibility: trafficFlowOn ? "visible" : "none" },
        },
        "user-accuracy"
      );
    }

    // TomTom Incidents
    if (!map.getSource(TOMTOM_INC_SRC)) {
      map.addSource(TOMTOM_INC_SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "tomtom-incidents-halo",
        type: "circle",
        source: TOMTOM_INC_SRC,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["coalesce", ["get", "magnitudeOfDelay"], 1], 1, 10, 4, 18],
          "circle-color": ["coalesce", ["get", "color"], "#64748B"],
          "circle-opacity": 0.18,
        },
        layout: { visibility: tomtomIncidentsOn ? "visible" : "none" },
      });
      map.addLayer({
        id: "tomtom-incidents-dot",
        type: "circle",
        source: TOMTOM_INC_SRC,
        paint: {
          "circle-radius": 7,
          "circle-color": ["coalesce", ["get", "color"], "#64748B"],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2.5,
        },
        layout: { visibility: tomtomIncidentsOn ? "visible" : "none" },
      });
    }

    // Crowd-sourced accidents (ON by default)
    if (!map.getSource(CROWD_ACC_SRC)) {
      map.addSource(CROWD_ACC_SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({
        id: "crowd-accidents-halo",
        type: "circle",
        source: CROWD_ACC_SRC,
        paint: { "circle-radius": 22, "circle-color": "#EAB308", "circle-opacity": 0.18, "circle-stroke-color": "#EAB308", "circle-stroke-width": 1.5, "circle-stroke-opacity": 0.45 },
        layout: { visibility: crowdAccidentsOn ? "visible" : "none" },
      });
      map.addLayer({
        id: "crowd-accidents-dot",
        type: "circle",
        source: CROWD_ACC_SRC,
        paint: { "circle-radius": 8, "circle-color": "#EAB308", "circle-stroke-color": "#0F1428", "circle-stroke-width": 2.5 },
        layout: { visibility: crowdAccidentsOn ? "visible" : "none" },
      });
    }
  };

  // ── Init map ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!webglOK || !containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getStyle(layer),
      center: [CITIZEN_FALLBACK.lng, CITIZEN_FALLBACK.lat],
      // Iter77 — Default zoom raised from 5.3 (whole Mali, mostly Sahara)
      // to 11 (city detail). Combined with CITIZEN_FALLBACK (Bamako, not
      // the country-wide FALLBACK used by the admin national map), a
      // citizen who denies GPS now sees their capital instead of empty
      // desert. When GPS resolves we `flyTo` the real fix at zoom 16.
      zoom: 11,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      pitchWithRotate: true,
      dragRotate: true,
      touchZoomRotate: true,
      doubleClickZoom: true,
      maxPitch: 60,
    });
    // Iter85 — Cap zoom at Esri's real native resolution (19) while
    // Satellite is active, so pinching further doesn't stretch the last
    // tile into a blurry "fake zoom" (the exact bug we're fixing).
    applySatelliteZoomCap(map, layer);
    // Iter79 — MapLibre attribution shows the base-tile provider
    // (MapTiler / OSM). We ADDITIONALLY inject a "POI · Google" tag via
    // `customAttribution` because our POI overlay is Google Places data
    // cached under the ToS. Required by Google Maps Platform TOS
    // §3.2.3(c) when displaying Places data on a third-party base map.
    map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution:
          '<span style="opacity:0.85">POI · <a href="https://policies.google.com/terms" target="_blank" rel="noopener">Google</a></span>',
      }),
      "bottom-right",
    );
    // Iter82 — Removed the top-right zoom +/- control per user request
    // (pinch-to-zoom + the bottom-left compass/tilt control remain).
    // Iter69 — Modern map controls: tilt + rotation + reset-north compass.
    // `visualizePitch: true` shows the pitch indicator on the compass so
    // citizens can see the tilt state at a glance and tap to reset both
    // bearing and pitch to 0. `showZoom: false` because the existing
    // floating FABs already cover zoom on mobile and we want to keep the
    // top-right corner light.
    //
    // Iter70 — Moved from top-right to bottom-left so the segmented
    // map-style selector (which lives in the top status row) doesn't
    // overlap or hide the compass on 390–430 px viewports.
    map.addControl(
      new maplibregl.NavigationControl({ visualizePitch: true, showZoom: false, showCompass: true }),
      "bottom-left",
    );
    mapRef.current = map;
    // Opt-in debug handle for QA/automation — mirrors the `rr_poi_debug`
    // pattern from useFacilities.js. Zero effect for normal users.
    try {
      if (localStorage.getItem("rr_poi_debug") === "1") window.__rrMap = map;
    } catch (_e) { /* noop */ }

    const ro = new ResizeObserver(() => {
      try { map.resize(); }
      catch (e) { console.warn("[MapPage] map.resize() noop:", e?.message || e); }
    });
    ro.observe(containerRef.current);

    map.on("load", () => {
      // Iter86 — Country Focus Mask must render BELOW every operational
      // overlay, so overlay installation is gated on the (cached,
      // near-instant after the first map) boundary fetch instead of
      // racing it — guarantees z-order regardless of network timing.
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        // Install 3D buildings AFTER vector style finishes loading — skipped
        // entirely for hybrid/Satellite (see mapStyles.js comment).
        installBuildingExtrusion(map, layer);
        // All sources/layers (GPS dot, POI services, heatmap, route,
        // traffic, incidents, crowd-accidents) — shared with the
        // post-`setStyle()` re-install below so both paths stay identical.
        installOverlayLayers(map);
      });

      // Click handlers for incidents + crowd accidents
      const showIncidentPopup = (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties || {};
        const coords = f.geometry?.type === "LineString"
          ? f.geometry.coordinates[Math.floor(f.geometry.coordinates.length / 2)]
          : f.geometry?.coordinates;
        if (!coords) return;
        const label = p.label || "Incident";
        const color = p.color || "#64748B";
        const delayMin = p.delay ? Math.round(Number(p.delay) / 60) : null;
        const len = p.length ? `${Math.round(Number(p.length))} m` : "";
        const html = `
          <div style="font-family:system-ui;min-width:180px;max-width:240px">
            <div style="font-size:13px;font-weight:800;color:${color}">${label}</div>
            ${p.from || p.to ? `<div style="font-size:11px;color:#475569;margin-top:2px">${p.from || ""}${p.from && p.to ? " → " : ""}${p.to || ""}</div>` : ""}
            ${delayMin ? `<div style="font-size:11px;margin-top:4px">Retard ≈ ${delayMin} min</div>` : ""}
            ${len ? `<div style="font-size:11px">Longueur ${len}</div>` : ""}
          </div>`;
        new maplibregl.Popup({ closeButton: true, maxWidth: "260px" })
          .setLngLat(coords)
          .setHTML(html)
          .addTo(map);
      };
      map.on("click", "tomtom-incidents-dot", showIncidentPopup);
      map.on("mouseenter", "tomtom-incidents-dot", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "tomtom-incidents-dot", () => { map.getCanvas().style.cursor = ""; });

      map.on("click", "crowd-accidents-dot", (e) => {
        const f = e.features?.[0]; if (!f) return;
        const p = f.properties || {};
        const [lng, lat] = f.geometry.coordinates;
        const ts = p.created_at ? new Date(p.created_at).toLocaleString("fr-FR") : "";
        const html = `
          <div style="font-family:system-ui;min-width:180px;max-width:240px">
            <div style="font-size:13px;font-weight:800;color:#A16207">Accident signalé</div>
            <div style="font-size:10px;color:#64748B;margin-top:2px">Citoyens · ${ts}</div>
            ${p.description ? `<div style="font-size:11px;margin-top:6px;color:#0F1428">${p.description}</div>` : ""}
          </div>`;
        new maplibregl.Popup({ closeButton: true, maxWidth: "260px" })
          .setLngLat([lng, lat])
          .setHTML(html)
          .addTo(map);
      });
      map.on("mouseenter", "crowd-accidents-dot", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "crowd-accidents-dot", () => { map.getCanvas().style.cursor = ""; });

      // Click on a service → popup with TWO actions: in-app turn-by-turn (TomTom traffic-aware) + external Google Maps
      map.on("click", "svc-bg", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties;
        const [lng, lat] = f.geometry.coordinates;
        const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        const popup = new maplibregl.Popup({ closeButton: true, maxWidth: "270px" })
          .setLngLat([lng, lat])
          .setHTML(`
            <div style="font-family:system-ui;min-width:210px;max-width:250px">
              <div style="font-size:13px;font-weight:800;color:${p.color};letter-spacing:0.01em">${p.name}</div>
              <div style="font-size:11px;color:#64748B;margin-top:2px">${p.city || ""}</div>
              <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
                <button data-route-svc="1" data-testid="svc-popup-route"
                   style="font-size:11px;font-weight:700;padding:6px 10px;background:#10B981;color:#fff;border:none;border-radius:8px;cursor:pointer">
                   Itinéraire
                </button>
                <a href="${dirUrl}" target="_blank" rel="noopener"
                   style="font-size:11px;font-weight:700;padding:6px 10px;background:#0F1428;color:#fff;border-radius:8px;text-decoration:none">
                   Google Maps
                </a>
                ${p.phone ? `<a href="tel:${p.phone}" data-testid="svc-popup-call"
                   style="font-size:11px;font-weight:700;padding:6px 10px;background:#DC2626;color:#fff;border-radius:8px;text-decoration:none">
                   ${p.phone}
                </a>` : ""}
              </div>
            </div>`)
          .addTo(map);
        // Wire the in-app Itinéraire button (popups are detached DOM)
        const el = popup.getElement().querySelector('[data-route-svc="1"]');
        if (el) {
          el.addEventListener("click", () => {
            popup.remove();
            startRouteToService({ name: p.name, city: p.city, lat, lng });
          });
        }
      });
      map.on("mouseenter", "svc-bg", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "svc-bg", () => { map.getCanvas().style.cursor = ""; });

      // Iter84 — Tap a cluster badge to zoom in until it breaks apart
      // into individual POI icons.
      map.on("click", "svc-clusters", (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const clusterId = f.properties.cluster_id;
        map.getSource(SVC_SOURCE)
          .getClusterExpansionZoom(clusterId)
          .then((zoom) => {
            map.easeTo({ center: f.geometry.coordinates, zoom: zoom + 0.5, duration: 500 });
          })
          .catch(() => { /* noop — cluster already expanded */ });
      });
      map.on("mouseenter", "svc-clusters", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "svc-clusters", () => { map.getCanvas().style.cursor = ""; });

      // Iter68 — Long-press / right-click "drop a report pin" handlers
      // removed. Citizens can no longer create an alert from an
      // arbitrary map point — emergency alerts must come from the
      // real GPS via the SOS button on the home screen.
    });

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      flewOnceRef.current = false;
      layerMountedRef.current = false;
    };
  }, []);

  // ── Fly to user position on first fix (smooth entry animation) ────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !coords) return;
    const ringColor = accColor(coords.accuracy);
    accColorRef.current = ringColor;

    const apply = () => {
      const src = map.getSource(USER_SOURCE);
      if (src) {
        src.setData({
          type: "FeatureCollection",
          features: [{
            type: "Feature",
            properties: { accuracy: coords.accuracy || 0, ringColor },
            geometry: { type: "Point", coordinates: [coords.longitude, coords.latitude] },
          }],
        });
      }
      if (!flewOnceRef.current) {
        flewOnceRef.current = true;
        map.flyTo({
          center: [coords.longitude, coords.latitude],
          zoom: 16,
          // Satellite stays strictly 2D (no auto-tilt) so the raw imagery
          // reads cleanly — tilt is a "Plan"/"Sombre"-only cinematic touch.
          pitch: layer === "hybrid" ? 0 : 45,
          bearing: 0,
          speed: 0.85,
          curve: 1.55,
          essential: true,
          easing: (t) => 1 - Math.pow(1 - t, 4),  // ease-out-quart for premium feel
        });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [coords]);

  // ── Layer style swap ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Skip the first run: the map was already constructed with this
    // style. Every subsequent change to `layer` must call setStyle —
    // independent of whether we have a GPS fix yet (Iter72 bug-fix).
    if (!layerMountedRef.current) {
      layerMountedRef.current = true;
      return;
    }
    map.setStyle(getStyle(layer));
    applySatelliteZoomCap(map, layer);
    // After style swap, all our sources/layers vanish — we'd need to re-add them.
    // Simpler: reload page since the user is on a dedicated map tab. To avoid
    // jarring UX we just defer re-adding everything on the next `styledata`.
    map.once("styledata", () => {
      getCountryBoundary().then((boundary) => {
        try {
          installCountryMask(map, boundary);
          installBuildingExtrusion(map, layer);
          // Re-apply the premium pitch so 3D buildings + cinematic tilt persist
          // across style swaps. Satellite (hybrid) stays strictly 2D — no
          // buildings AND no auto-tilt, so the raw imagery is unobstructed.
          // Unconditional (not gated on `coords`) so this also applies with
          // GPS denied/pending.
          map.easeTo({
            pitch: layer === "hybrid" ? 0 : 45,
            duration: 800,
            easing: (t) => 1 - Math.pow(1 - t, 3),
          });
          // Re-install every overlay (GPS dot, POI services, heatmap,
          // route, traffic, incidents, crowd-accidents) — this is the
          // Iter82 fix: previously only USER_SOURCE was restored here, so
          // hospitals/police/embassies (and every other overlay) vanished
          // on any Plan/Satellite/Sombre switch away from the initial style.
          installOverlayLayers(map);
        } catch (_e) { /* noop */ }
      });
    });
  }, [layer]);

  // ── Sync service layer when toggles OR live facility list changes ────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource(SVC_SOURCE);
    if (src) src.setData(buildServicesGeoJSON(activeServiceKeys, facilities));
  }, [activeServiceKeys, facilities]);

  // ── Heatmap fetch + toggle ────────────────────────────────────────────
  useEffect(() => {
    if (!heatmapOn) return undefined;
    let cancelled = false;
    fetchSafetyHeatmap(168).then((data) => {
      if (cancelled) return;
      setHeatmapData(data);
      const map = mapRef.current;
      if (!map) return;
      const features = (data.cells || []).map((c) => ({
        type: "Feature",
        properties: { count: c.count },
        geometry: { type: "Point", coordinates: [c.longitude, c.latitude] },
      }));
      const apply = () => {
        const src = map.getSource(HEAT_SOURCE);
        if (src) src.setData({ type: "FeatureCollection", features });
        if (map.getLayer("safety-heat")) map.setLayoutProperty("safety-heat", "visibility", "visible");
      };
      if (map.isStyleLoaded()) apply();
      else map.once("styledata", apply);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [heatmapOn]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!heatmapOn && map.getLayer("safety-heat")) {
      try { map.setLayoutProperty("safety-heat", "visibility", "none"); }
      catch (e) { console.warn("[MapPage] safety-heat visibility toggle noop:", e?.message || e); }
    }
  }, [heatmapOn]);

  // ── Traffic Flow visibility ────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (map.getLayer("tomtom-flow-layer")) {
        try { map.setLayoutProperty("tomtom-flow-layer", "visibility", trafficFlowOn ? "visible" : "none"); }
        catch (e) { console.warn("[MapPage] tomtom-flow-layer visibility noop:", e?.message || e); }
      }
    };
    if (map.isStyleLoaded()) apply(); else map.once("styledata", apply);
  }, [trafficFlowOn]);

  // ── TomTom Incidents: visibility + viewport-bound polling ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const apply = () => {
      ["tomtom-incidents-halo", "tomtom-incidents-dot"].forEach((id) => {
        if (map.getLayer(id)) {
          try { map.setLayoutProperty(id, "visibility", tomtomIncidentsOn ? "visible" : "none"); }
          catch (e) { console.warn(`[MapPage] ${id} visibility noop:`, e?.message || e); }
        }
      });
    };
    if (map.isStyleLoaded()) apply(); else map.once("styledata", apply);

    if (!tomtomIncidentsOn) return undefined;
    let cancelled = false;
    let pollId = null;

    const fetchOnce = async () => {
      if (cancelled) return;
      const b = map.getBounds();
      const sw = b.getSouthWest(); const ne = b.getNorthEast();
      const bbox = `${sw.lng.toFixed(4)},${sw.lat.toFixed(4)},${ne.lng.toFixed(4)},${ne.lat.toFixed(4)}`;
      try {
        const r = await fetch(`${API}/map/traffic/incidents?bbox=${encodeURIComponent(bbox)}`);
        if (!r.ok) return;
        const fc = await r.json();
        // Decorate features: each TomTom feature has properties.iconCategory.
        // We attach `color` + `label` so MapLibre paints + popup can read them
        // directly without recomputing in JS.
        const enriched = (fc.features || []).map((f) => {
          const cat = f.properties?.iconCategory;
          const meta = tomtomMeta(cat);
          return {
            ...f,
            geometry: f.geometry?.type === "LineString"
              ? { type: "Point", coordinates: f.geometry.coordinates[Math.floor(f.geometry.coordinates.length / 2)] }
              : f.geometry,
            properties: { ...f.properties, color: meta.color, label: meta.label },
          };
        }).filter((f) => f.geometry && f.geometry.coordinates);
        if (cancelled) return;
        const src = map.getSource(TOMTOM_INC_SRC);
        if (src) src.setData({ type: "FeatureCollection", features: enriched });
      } catch (e) { console.warn("[MapPage] TomTom incidents refresh failed:", e?.message || e); }
    };
    fetchOnce();
    pollId = setInterval(fetchOnce, 60000);  // TomTom refreshes ~once/minute
    const onMoveEnd = () => { fetchOnce(); };
    map.on("moveend", onMoveEnd);
    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      map.off("moveend", onMoveEnd);
    };
  }, [tomtomIncidentsOn]);

  // ── Crowd-sourced accidents: visibility + polling ─────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const apply = () => {
      ["crowd-accidents-halo", "crowd-accidents-dot"].forEach((id) => {
        if (map.getLayer(id)) {
          try { map.setLayoutProperty(id, "visibility", crowdAccidentsOn ? "visible" : "none"); }
          catch (e) { console.warn(`[MapPage] ${id} visibility noop:`, e?.message || e); }
        }
      });
    };
    if (map.isStyleLoaded()) apply(); else map.once("styledata", apply);
    if (!crowdAccidentsOn) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/map/crowd-accidents?since_hours=24`);
        if (!r.ok) return;
        const fc = await r.json();
        if (cancelled) return;
        const src = map.getSource(CROWD_ACC_SRC);
        if (src) src.setData(fc);
      } catch (e) { console.warn("[MapPage] crowd-accidents refresh failed:", e?.message || e); }
    };
    tick();
    const id = setInterval(tick, 90000);  // every 90s — these are user-generated, low churn
    return () => { cancelled = true; clearInterval(id); };
  }, [crowdAccidentsOn]);

  // ── Report pin sync ───────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource(PIN_SOURCE);
    const fc = reportPin
      ? { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [reportPin.lng, reportPin.lat] } }] }
      : { type: "FeatureCollection", features: [] };
    if (src) src.setData(fc);
  }, [reportPin]);

  // ── Recenter handler ──────────────────────────────────────────────────
  const recenter = () => {
    const map = mapRef.current;
    if (!map) return;
    const easing = (t) => 1 - Math.pow(1 - t, 4);
    // Satellite stays strictly 2D — no auto-tilt on recenter either.
    const tiltedPitch = layer === "hybrid" ? 0 : 40;
    if (coords) {
      map.flyTo({ center: [coords.longitude, coords.latitude], zoom: 16, pitch: tiltedPitch, speed: 1.2, curve: 1.5, essential: true, easing });
    } else {
      map.flyTo({ center: [CITIZEN_FALLBACK.lng, CITIZEN_FALLBACK.lat], zoom: 11, pitch: 0, speed: 1.2, essential: true, easing });
    }
  };

  const handleClearRoute = () => {
    const map = mapRef.current;
    if (map) clearRoute(map);
    setRouteData(null);
    setRouteDest(null);
  };

  const toggleService = (key) => {
    setActiveServiceKeys((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  // Iter68 — `onReportFromPin` removed: long-press alert creation is
  // gone, so there's no pin to report from anymore.

  const accLabel = useMemo(() => {
    if (!coords) return null;
    const a = Math.round(coords.accuracy || 0);
    if (a <= 20) return { txt: `±${a} m · ÉLEVÉE`, color: "#10B981" };
    if (a <= 100) return { txt: `±${a} m · MOYENNE`, color: "#F59E0B" };
    return { txt: `±${a} m · FAIBLE`, color: "#EF4444" };
  }, [coords]);

  if (!webglOK) {
    return (
      <div className="flex-1 flex flex-col bg-background text-foreground" data-testid="map-page">
        <div className="p-6 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <p className="text-sm font-bold">Carte indisponible</p>
          <p className="text-xs text-muted-foreground mt-1">WebGL n&apos;est pas supporté par ce navigateur.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-background text-foreground relative" data-testid="map-page">
      {/* Map fills the area. Iter77 — removed hard-coded paddingBottom
          + min-h-[70vh] which left a large empty band between the map
          canvas and the bottom nav. The PhoneShell already applies
          `pb-24` on citizen routes, so the flex-1 map wrapper below
          naturally sits above the nav with no white gap. */}
      <div className="relative flex-1">
        <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} data-testid="map-canvas-wrap" />

        {/* Top status pill */}
        <div className="absolute top-3 left-3 right-3 z-[460] flex items-start gap-2 pointer-events-none">
          {coords ? (
            <div className="flex-1 bg-background/95 backdrop-blur rounded-2xl border border-border shadow-md px-1.5 py-1 pointer-events-auto" data-testid="map-status-card">
              <div className="flex items-center gap-1.5">
                <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accLabel.color}20`, color: accLabel.color }}>
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: accLabel.color }}>{t.positionDetected}</div>
                  <div className="font-mono text-[10px] truncate">{coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)} · {accLabel.txt}</div>
                </div>
                <button
                  type="button"
                  onClick={refreshGps}
                  data-testid="map-refresh-gps"
                  title="Améliorer ma position"
                  className="shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] border border-border rounded-lg hover:bg-muted active:scale-95 transition-transform inline-flex items-center gap-1"
                >
                  <Navigation2 className="w-3 h-3" strokeWidth={2.5} />
                  Améliorer
                </button>
              </div>
              {/* Iter69 — Low-precision warning. Shown when the GPS
                  accuracy radius is above 50 m (anything more is
                  basically cell-tower / WiFi grade, not GPS). */}
              {(coords.accuracy || 0) > 50 && (
                <div
                  data-testid="map-gps-approx-banner"
                  className="mt-1 text-[10px] text-amber-700 font-semibold flex items-center gap-1.5"
                >
                  <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                  Position approximative — précision GPS: ±{Math.round(coords.accuracy)} m
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 bg-background/95 backdrop-blur rounded-2xl border border-border shadow-md px-3 py-2 flex items-center gap-2 pointer-events-auto" data-testid="map-status-card-pending">
              <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-700">
                  {error === "denied" ? t.locationBlocked : t.positionSearching}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {error === "denied" ? t.locationBlockedDetail : t.waitingGps}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Iter78 — Map style selector (Plan / Satellite / Sombre) relocated
            from the top status row to the bottom-left corner, stacked just
            above the MapLibre tilt/compass control. This declutters the
            top status pill (which was crushing the accuracy card on narrow
            viewports) and consolidates all map-mode controls in one
            corner. Popover now opens UPWARD so it doesn't clip the
            bottom nav. `bottom-[104px]` sits comfortably above the
            compass (~10 px margin + ~29 px button) with room to breathe. */}
        <div
          className="absolute left-2 bottom-[104px] z-[455] pointer-events-auto"
          data-testid="map-layer-selector"
        >
          {(() => {
            const STYLES = [
              { key: "street", label: "Plan" },
              { key: "hybrid", label: "Satellite" },
              { key: "dark",   label: "Sombre" },
            ];
            const activeOpt = STYLES.find((s) => s.key === layer) || STYLES[0];
            return (
              <div className="relative">
                <button
                  type="button"
                  data-testid="map-layer-toggle"
                  onClick={() => setLayerMenuOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={layerMenuOpen}
                  aria-label={`Style de carte : ${activeOpt.label}`}
                  title={`Style de carte : ${activeOpt.label}`}
                  className="h-10 shrink-0 inline-flex items-center gap-1.5 px-2.5 sm:px-3 bg-background/95 backdrop-blur rounded-2xl border border-border shadow-md text-[10px] font-bold uppercase text-foreground hover:bg-muted active:scale-95 transition-transform"
                >
                  <Layers className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
                  <span className="hidden sm:inline whitespace-nowrap">{activeOpt.label}</span>
                </button>
                {layerMenuOpen && (
                  <>
                    {/* Click-outside backdrop — transparent, full-screen
                        so a tap anywhere closes the popover. */}
                    <button
                      type="button"
                      aria-label="Fermer le sélecteur de style"
                      onClick={() => setLayerMenuOpen(false)}
                      className="fixed inset-0 z-[440] cursor-default bg-transparent"
                      data-testid="map-layer-menu-backdrop"
                    />
                    <div
                      role="listbox"
                      aria-label="Style de carte"
                      data-testid="map-layer-menu"
                      className="absolute left-0 bottom-[44px] z-[450] min-w-[140px] bg-background border border-border rounded-xl shadow-xl overflow-hidden"
                    >
                      {STYLES.map((opt) => {
                        const active = layer === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            role="option"
                            aria-selected={active}
                            data-testid={`map-layer-${opt.key}`}
                            onClick={() => { setLayer(opt.key); setLayerMenuOpen(false); }}
                            className={`w-full text-left px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                              active ? "bg-mali-navy text-white" : "text-foreground hover:bg-muted"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* Iter75 — POI search dropdown. A minimal client-side filter
            over `EMERGENCY_SERVICES` (name / city / category, accent-
            insensitive). Anchored on the top-left of the map, below the
            status row. Collapsed by default: just a magnifying-glass
            FAB. Tapping expands the input + a scrollable results list.
            Selecting a result flies the map to the POI + auto-collapses. */}
        <MapSearchBar
          onFlyTo={(poi) => {
            const map = mapRef.current;
            if (!map) return;
            map.flyTo({ center: [poi.lng, poi.lat], zoom: 16, essential: true, speed: 1.3, curve: 1.4 });
            // Also make sure the POI's category is toggled ON so the
            // dot is visible; otherwise the citizen flies to a
            // location with no marker underneath.
            const cat = poi.type || poi.category;
            setActiveServiceKeys((prev) => {
              if (prev.has(cat)) return prev;
              const next = new Set(prev);
              next.add(cat);
              return next;
            });
          }}
          query={searchQuery}
          setQuery={setSearchQuery}
          facilities={facilities}
        />

        {/* Service category pills (horizontal scroll). Iter77 — right
            padding raised to `right-14` so the horizontally-scrolling
            chip row no longer runs under the search-icon FAB anchored
            at `top-[68px] right-2`. Prevents the last chip from being
            visually clipped by the search button. */}
        <div className="absolute top-[68px] left-2 right-14 z-[400] flex gap-1.5 overflow-x-auto pb-1 pointer-events-auto no-scrollbar" data-testid="svc-pills">
          {SERVICE_CATEGORIES.map((s) => {
            const active = activeServiceKeys.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleService(s.key)}
                data-testid={`svc-toggle-${s.key}`}
                aria-pressed={active}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border-2 transition-all ${
                  active ? "bg-background text-foreground shadow-md" : "bg-background/60 text-muted-foreground"
                }`}
                style={active ? { borderColor: s.color, color: s.color } : { borderColor: "rgba(100,116,139,0.3)" }}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.short}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setHeatmapOn((v) => !v)}
            data-testid="heatmap-toggle"
            aria-pressed={heatmapOn}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border-2 transition-all ${
              heatmapOn ? "bg-red-50 border-red-500 text-red-600 shadow-md" : "bg-background/60 border-border text-muted-foreground"
            }`}
          >
            <FlameIcon className="w-3 h-3" /> Zones actives
          </button>
          <button
            type="button"
            onClick={() => setTrafficFlowOn((v) => !v)}
            data-testid="traffic-flow-toggle"
            aria-pressed={trafficFlowOn}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border-2 transition-all ${
              trafficFlowOn ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-md" : "bg-background/60 border-border text-muted-foreground"
            }`}
          >
            <Car className="w-3 h-3" /> Trafic
          </button>
          <button
            type="button"
            onClick={() => setTomtomIncidentsOn((v) => !v)}
            data-testid="tomtom-incidents-toggle"
            aria-pressed={tomtomIncidentsOn}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border-2 transition-all ${
              tomtomIncidentsOn ? "bg-orange-50 border-orange-500 text-orange-700 shadow-md" : "bg-background/60 border-border text-muted-foreground"
            }`}
          >
            <Construction className="w-3 h-3" /> Incidents
          </button>
          <button
            type="button"
            onClick={() => setCrowdAccidentsOn((v) => !v)}
            data-testid="crowd-accidents-toggle"
            aria-pressed={crowdAccidentsOn}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border-2 transition-all ${
              crowdAccidentsOn ? "bg-amber-50 border-amber-500 text-amber-700 shadow-md" : "bg-background/60 border-border text-muted-foreground"
            }`}
          >
            <AlertTriangle className="w-3 h-3" /> Accidents
          </button>
        </div>

        {/* Iter68 — Long-press hint + report-pin card removed. Citizens
            can no longer create an alert from an arbitrary map point;
            the SOS button on the home screen captures the real GPS. */}

        {/* Floating action cluster (bottom-right): live share + recenter */}
        <div className="absolute right-3 bottom-[100px] z-[450] flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setLivePanelOpen(true)}
            data-testid="map-live-share-fab"
            className={`relative w-12 h-12 rounded-full shadow-xl flex items-center justify-center border-2 transition-all ${
              liveShare.active ? "bg-emerald-600 border-emerald-700 text-white" : "bg-background border-border text-foreground"
            }`}
            aria-label="Partage en direct"
          >
            {liveShare.active ? <Radio className="w-5 h-5" /> : <Radio className="w-5 h-5" />}
            {liveShare.active && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-background animate-pulse" />
            )}
          </button>
          <button
            type="button"
            onClick={recenter}
            data-testid="map-recenter-fab"
            className="w-12 h-12 rounded-full shadow-xl bg-mali-navy text-white flex items-center justify-center border-2 border-mali-navy hover:opacity-95 transition-opacity"
            aria-label="Recentrer"
          >
            <Navigation2 className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <LiveSharePanel
        open={livePanelOpen}
        onOpenChange={setLivePanelOpen}
        sessionApi={liveShare}
        coords={coords}
      />
      <RouteDrawer
        open={routeOpen}
        onOpenChange={setRouteOpen}
        loading={routeLoading}
        route={routeData}
        destination={routeDest}
        onClear={handleClearRoute}
      />
    </div>
  );
}

// Re-export for callers that grab the MapPin icon from this module
export { MapPin };

// Iter75 — Emergency-services POI search bar.
//
// Rendered by MapPage as a small floating FAB at the top of the map.
// Collapsed by default (a single magnifying-glass button) so it never
// covers the map. Tapping expands into a full-width input + result
// dropdown. Client-side only — no network round-trips — filtering
// EMERGENCY_SERVICES by name / city / category (accent-insensitive).
function stripAccents(s) {
  try { return s.normalize("NFD").replace(/\p{Diacritic}/gu, ""); }
  catch { return s; }
}
function MapSearchBar({ onFlyTo, query, setQuery, facilities }) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const results = React.useMemo(() => {
    const q = stripAccents((query || "").trim().toLowerCase());
    if (!q) return [];
    const source = Array.isArray(facilities) && facilities.length ? facilities : EMERGENCY_SERVICES;
    return source.filter((p) => {
      const cat = p.type || p.category;
      const hay = stripAccents(`${p.name} ${p.city || ""} ${SERVICE_META[cat]?.label || ""}`.toLowerCase());
      return hay.includes(q);
    }).slice(0, 15);
  }, [query, facilities]);

  return (
    <div className="absolute top-[68px] right-2 z-[430] pointer-events-auto" data-testid="map-search">
      {!open ? (
        <button
          type="button"
          data-testid="map-search-toggle"
          onClick={() => setOpen(true)}
          aria-label="Rechercher un lieu d'aide"
          className="h-10 w-10 inline-flex items-center justify-center bg-background/95 backdrop-blur border border-border rounded-full shadow-md hover:bg-muted active:scale-95 transition-transform"
        >
          <Search className="w-4 h-4" strokeWidth={2.5} />
        </button>
      ) : (
        <div className="w-[260px] max-w-[calc(100vw-24px)] bg-background/98 backdrop-blur border border-border rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 h-10 border-b border-border">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              data-testid="map-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hôpital, police, ambassade…"
              className="flex-1 bg-transparent border-none outline-none text-[13px] font-medium placeholder:text-muted-foreground text-foreground"
              aria-label="Rechercher un lieu"
            />
            <button
              type="button"
              onClick={() => { setQuery(""); setOpen(false); }}
              data-testid="map-search-close"
              aria-label="Fermer la recherche"
              className="shrink-0 p-1 -mr-1 hover:bg-muted rounded"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          {query.trim() !== "" && (
            <div
              className="max-h-[280px] overflow-y-auto"
              data-testid="map-search-results"
            >
              {results.length === 0 ? (
                <div className="px-3 py-4 text-[12px] italic text-muted-foreground">
                  Aucun lieu trouvé.
                </div>
              ) : (
                results.map((p) => {
                  const cat = p.type || p.category;
                  const meta = SERVICE_META[cat] || {};
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        onFlyTo(p);
                        setOpen(false);
                        setQuery("");
                      }}
                      data-testid={`map-search-result-${p.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2 border-b border-border/50 last:border-b-0"
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: meta.color || "#71717A" }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-bold text-foreground truncate">{p.name}</span>
                        <span className="block text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                          {meta.short || cat} · {p.city || ""}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

